import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Grid, Link as MuiLink, Typography } from '@mui/material';
import { Groups as IconGroups, People as IconPeople, Person as IconPerson
    , EscalatorWarning as IconEscalera, Sick as IconSick, AssignmentInd as IconTarea
    , Checklist as IconChecklist, Apartment as IconApartment } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';

import { navGroups, visiblesDe } from '../../menu';
import * as terapiaService from '../../services/terapiaService';
import * as sesionService from '../../services/sesionService';
import { getPersonaResumenBasicoById } from '../../services/personaService';
import { obtenerPerfil } from '../../services/usuarioService';
import { obtenerUsuario } from '../../services/authService';
import { useSelector } from 'react-redux';
import { useMantenimiento } from '../../hooks/useMantenimiento';

// Inicio del sistema: un tablero con las cifras del momento y, debajo, las
// opciones del sistema distribuidas en columnas.
//
// Las cifras dependen de quien entra:
//   * el terapeuta ve las suyas: las terapias que tiene asignadas y las
//     sesiones que ha registrado
//   * el resto ve las del sistema: participantes que llevan terapias, terapias
//     activas y terapeutas asignados
//
// Las opciones son las mismas del menu lateral, con el mismo filtro por
// accesos: lo que el usuario ve aqui es lo que puede usar.
const Dashboard = () => {
  useMantenimiento();
  const navigate = useNavigate();
  const usuario = obtenerUsuario();
  const accesos = useSelector((state) => state.seguridad?.accesos) || [];
  const roles = useSelector((state) => state.seguridad?.roles) || [];
  const seguridadActiva = useSelector((state) => state.seguridad?.seguridadActiva) || "N";

  const [cifras, setCifras] = useState({ participantes: 0, terapias: 0, terapeutas: 0, sesiones: 0 });
  const [esTerapeuta, setEsTerapeuta] = useState(false);
  const [cargando, setCargando] = useState(true);

  // Cifras generales del sistema: del resumen de terapias activas salen las
  // tres, porque cada fila es una terapia con su participante y su terapeuta.
  const fetchCifrasGenerales = useCallback(async () => {
    const data = await terapiaService.getsTerapiaResumenView({
      idFilial: 0, member: "", therapist: "", idEstado: 2, idTerapeuta: 0,
    }).catch((err) => { console.log("Err: ", err); });

    // Set, no lista: una terapia puede tener mas de un participante y mas de
    // un terapeuta, y se cuentan una sola vez.
    const participantes = new Set();
    const terapias = new Set();
    const terapeutas = new Set();

    for (const fila of data || []) {
      if (fila.idParticipante != null) participantes.add(fila.idParticipante);
      if (fila.idTerapia != null) terapias.add(fila.idTerapia);
      if (fila.idTerapeuta != null) terapeutas.add(fila.idTerapeuta);
    }

    setCifras({
      participantes: participantes.size,
      terapias: terapias.size,
      terapeutas: terapeutas.size,
    });
  }, [])

  // Cifras del terapeuta: sus terapias y sus sesiones.
  const fetchCifrasTerapeuta = useCallback(async (idTerapeuta) => {
    const [terapias, sesiones] = await Promise.all([
      terapiaService.getsTerapiaResumenView({
        idFilial: 0, member: "", therapist: "", idEstado: 2, idTerapeuta: idTerapeuta,
      }).catch((err) => { console.log("Err: ", err); }),
      sesionService.getsSesionResumenView({
        idTerapeuta: idTerapeuta, participante: "", idPeriodo: 0,
        fechaInicio: "", fechaFin: "", idEstado: 0,
      }).catch((err) => { console.log("Err: ", err); }),
    ]);

    setCifras({
      terapias: (terapias || []).length,
      sesiones: (sesiones || []).length,
    });
  }, [])

  useEffect(() => {
    const cargar = async () => {
      const perfil = await obtenerPerfil().catch(() => null);
      const idPersona = perfil?.idPersona || 0;

      // Si la persona del usuario es terapeuta (idTipoPersona = 25), el
      // tablero es el suyo.
      if (idPersona > 0) {
        const tipo = await getPersonaResumenBasicoById(idPersona).catch(() => null);

        if (tipo === 25 || accesos.includes("sesionTerapeuta.ver") || accesos.includes("terapiaPlanTerapeuta.ver")) {
          setEsTerapeuta(true);
          await fetchCifrasTerapeuta(idPersona);
          setCargando(false);
          return;
        }
      }

      await fetchCifrasGenerales();
      setCargando(false);
    };

    cargar();
  }, [fetchCifrasGenerales, fetchCifrasTerapeuta])

  // Las opciones del menu, sin el propio Inicio, filtradas por los accesos
  // del usuario logueado usando la funcion centralizada visiblesDe.
  const grupos = useMemo(() => {
    return visiblesDe(
      navGroups.filter((grupo) => grupo.path !== "/"),
      accesos,
      seguridadActiva,
      usuario,
      roles
    );
  }, [accesos, seguridadActiva, usuario, roles]);

  // Accesos rapidos a lo que mas se usa, acotados por los permisos del usuario.
  const atajos = useMemo(() => {
    const lista = [
      {
        titulo: "Terapias",
        detalle: "Participantes, horarios y terapias activas",
        ruta: "/therapy",
        icono: <IconSick />,
        acceso: "terapia.ver",
      },
      {
        titulo: "Planes de Terapia",
        detalle: "Planes por terapia, con sus areas",
        ruta: accesos.includes("terapiaPlan.ver") ? "/therapyplan" : "/therapyplanterapist",
        icono: <IconEscalera />,
        acceso: ["terapiaPlan.ver", "terapiaPlanTerapeuta.ver"],
      },
      {
        titulo: "Sesiones de Terapeuta",
        detalle: "Registro de sesiones por periodo",
        ruta: esTerapeuta ? "/session/0" : "/session",
        icono: <IconTarea />,
        acceso: ["sesionTerapeuta.ver", "sesion.ver"],
      },
      {
        titulo: "Personas",
        detalle: "Participantes, terapeutas y contactos",
        ruta: "/person",
        icono: <IconPeople />,
        acceso: "persona.ver",
      },
      {
        titulo: "Filiales",
        detalle: "Sedes del sistema y sus direcciones",
        ruta: "/filial",
        icono: <IconApartment />,
        acceso: "filial.ver",
      },
    ];

    if (seguridadActiva !== "S") return lista;

    return lista.filter((item) => {
      if (!item.acceso) return true;
      const requeridos = Array.isArray(item.acceso) ? item.acceso : [item.acceso];
      return requeridos.some((acc) => accesos.includes(acc));
    });
  }, [accesos, seguridadActiva, esTerapeuta]);

  // Las cifras del terapeuta son las suyas; las del resto, las del sistema.
  const indicadores = esTerapeuta
    ? [
        { etiqueta: "Terapias asignadas", valor: cifras.terapias, icono: <IconSick />, color: "#00796b" },
        { etiqueta: "Sesiones registradas", valor: cifras.sesiones, icono: <IconChecklist />, color: "#e07b00" },
      ]
    : [
        { etiqueta: "Participantes en terapia", valor: cifras.participantes, icono: <IconGroups />, color: "#5b2d8e" },
        { etiqueta: "Terapias activas", valor: cifras.terapias, icono: <IconSick />, color: "#00796b" },
        { etiqueta: "Terapeutas asignados", valor: cifras.terapeutas, icono: <IconPerson />, color: "#e07b00" },
      ]

  // Una opcion del tablero: las que no tienen hijos son un enlace; las que
  // tienen (los subgrupos) son un titulo con sus opciones debajo.
  const Opcion = ({ item, sangria }) => {
    if (!item.children) {
      return (
        <MuiLink component={Link} to={item.path} underline="hover"
          sx={{ display: "block", color: "text.primary", fontSize: 14, py: 0.4, pl: sangria }}
        >
          {item.title}
        </MuiLink>
      );
    }

    return (
      <Box sx={{ pl: sangria }}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: 13, color: "text.secondary", py: 0.4 }}>
          {item.title}
        </Typography>
        {(item.children || []).map((hijo) => (
          <Opcion key={hijo.title} item={hijo} sangria={sangria + 1.5} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "grey.100", minHeight: "100vh", margin: -2, padding: 0 }}>
      {/* Cabecera: el nombre del sistema con su marca, como la portada. */}
      <Box sx={{
        background: "linear-gradient(120deg, #1976d2 0%, #5b2d8e 100%)",
        color: "white",
        padding: { xs: 3, sm: 5 },
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <img src={process.env.PUBLIC_URL + "/logosinletras.png"} alt="KOLOB Autismo Peru"
            style={{ height: "clamp(34px, 5vw, 56px)", width: "auto", display: "block" }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: "bold", fontSize: { xs: "1.5rem", sm: "2.1rem" } }}>
              Sistema Terap&eacute;utico
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {usuario ? "Conectado como " + usuario : "Gestion de terapias, planes y sesiones"}
              {esTerapeuta ? " - Terapeuta" : ""}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ padding: { xs: 2, sm: 3 } }}>
        {/* Cifras del momento */}
        <Grid container spacing={2}>
          {indicadores.map((indicador) => (
            <Grid item xs={12} sm={indicadores.length === 2 ? 6 : 4} key={indicador.etiqueta}>
              <Card variant="outlined" sx={{
                display: "flex", alignItems: "center", gap: 2, padding: 2,
                borderLeft: "4px solid " + indicador.color,
              }}>
                <Box sx={{ color: indicador.color, display: "flex" }}>{indicador.icono}</Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {indicador.etiqueta}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: "bold", color: indicador.color }}>
                    {cargando ? "-" : indicador.valor}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Opciones del sistema, en columnas */}
        <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 4, marginBottom: 1 }}>
          Opciones del sistema
        </Typography>
        <Grid container spacing={2}>
          {grupos.map((grupo) => (
            <Grid item xs={12} sm={6} md={4} key={grupo.title}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginBottom: 1 }}>
                    <Box sx={{ color: "primary.main", display: "flex" }}>{grupo.icon}</Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      {grupo.title}
                    </Typography>
                  </Box>
                  <Box>
                    {grupo.children
                      ? grupo.children.map((hijo) => (
                          <Opcion key={hijo.title} item={hijo} sangria={0} />
                        ))
                      : <Opcion item={grupo} sangria={0} />}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Acceso rapido a lo que mas se usa (solo opciones con acceso) */}
        {atajos.length > 0 && (
          <>
            <Typography variant="h6" sx={{ fontWeight: "bold", marginTop: 4, marginBottom: 1 }}>
              Accesos r&aacute;pidos
            </Typography>
            <Grid container spacing={2}>
              {atajos.map((atajo) => (
                <Grid item xs={12} sm={6} md={3} key={atajo.ruta}>
                  <Card variant="outlined" sx={{
                    height: "100%", cursor: "pointer", "&:hover": { borderColor: "primary.main" },
                  }}
                    onClick={() => navigate(atajo.ruta)}
                  >
                    <CardContent>
                      <Box sx={{ color: "primary.main", display: "flex", marginBottom: 1 }}>{atajo.icono}</Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{atajo.titulo}</Typography>
                      <Typography variant="body2" color="text.secondary">{atajo.detalle}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  )
}

export default Dashboard
